import { useState, useEffect, useCallback } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import moment from 'moment';
import { useOnsiteInterviewForm } from './hooks/useOnsiteInterviewForm';
import MobileAppModal from '../../../commonComponents/MobileAppModal';
import { getUserEducation } from '../../../../actions/licensesAction';
import { UserType } from '../../../../enums/UserType';
import { filterObjectKeys, showErrorMessage } from '../../../../utils';
import { userDaSubProfessions } from '../../../../constants';
import { ConfirmationModal } from './components/ConfirmationModal';
import { ChildOfficeSelect } from '../../../commonComponents/ChildOfficeSelect';
import { CalendarSelection } from '../../../commonComponents/CalendarSelection';
import { RowContainer } from './components/RowContainer';
import { createLoadingSelector } from '../../../../apis/selectors';
import { useGetPostingDetails } from '../../../../domain/PermanentJobs/useCases/useGetPostingDetails';
import { RemoteConfigUtils } from '../../../../utils/RemoteConfigUtils';

const loadingSelector = createLoadingSelector([
  'GET_ALL_METADATA',
  'GET_ALL_PROFESSIONS',
  'GET_CHILD_OFFICES',
]);

export const CreateOnsiteInterview = () => {
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const history = useHistory();
  const { jobId } = match.params;

  const allProfessions = useSelector((state) => state.jobs.allProfessions);
  const userEducation = useSelector((state) => state.users.userEducation);

  const timeLimitInMinutes = RemoteConfigUtils.getOnsiteInterviewThresholdInMinutes();

  const { data: posting, isInitialLoading: isLoadingDetails } = useGetPostingDetails({
    postingId: jobId,
  });

  const {
    formValues,
    handleChange,
    handleProfessionChange,
    handleBreakChange,
    handleSubmit,
    selectedDates,
    setSelectedDates,
    errorStatus,
    previewData,
    isSubmitting,
  } = useOnsiteInterviewForm({ posting });

  const isInitialLoading = useSelector((state) => loadingSelector(state));

  // UI state
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fetch education when profession changes
  useEffect(() => {
    dispatch(getUserEducation({ userType: UserType.HYG, profession: formValues.profession }));
  }, [dispatch, formValues.profession]);

  // Available specialties and procedures
  const availableSpecialties =
    Object.keys(filterObjectKeys(userEducation?.specialtiesAndProcedures, 'specialty')) || [];

  const shouldShowSubProfession = !!formValues.subProfession;
  const daSubProfessions = [{ code: 'none', name: 'None' }, ...userDaSubProfessions];

  const handleBreakResult = useCallback(
    (result) => {
      handleBreakChange(result.time, result.paid);
      setShowBreakModal(false);
    },
    [handleBreakChange],
  );

  const handleReviewInterviews = useCallback(() => {
    if (timeLimitInMinutes > 0 && formValues.localStart && formValues.localEnd) {
      const start = moment(formValues.localStart, 'HH:mm');
      const end = moment(formValues.localEnd, 'HH:mm');
      const durationInMinutes = end.diff(start, 'minutes');

      if (durationInMinutes > timeLimitInMinutes) {
        const timeLimitInHours = timeLimitInMinutes / 60;
        showErrorMessage({
          // eslint-disable-next-line max-len
          message: `Selected time exceeds ${timeLimitInHours}-hour limit. Update either time to proceed`,
        });

        return;
      }
    }

    setShowConfirmation(true);
  }, [formValues, timeLimitInMinutes]);

  const handleConfirmSubmission = useCallback(() => {
    handleSubmit(() => {
      setShowConfirmation(false);
      history.push(`/permanent-job/office/${posting?.officeId}/job/${posting?.id}/onsiteInterview`);
    });
  }, [handleSubmit, history, posting]);

  // Loading state
  if (isLoadingDetails || isInitialLoading) {
    return (
      <div
        style={{
          width: '100vw',
          height: '90vh',
          overflowY: 'auto',
          flexGrow: 1,
          minWidth: 750,
          marginTop: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner
            animation="border"
            role="status"
            variant="info"
            style={{
              height: 30,
              width: 30,
              marginLeft: 20,
              marginTop: 10,
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100vw',
        height: '90vh',
        overflowY: 'auto',
        flexGrow: 1,
        minWidth: 750,
        marginTop: 20,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflowY: 'auto',
        }}
      >
        {/* Title */}
        <h3 style={{ fontSize: '24px', marginTop: 50 }}>Create Onsite Interviews</h3>

        {/* Subtitle */}
        <h4 style={{ textAlign: 'center', marginTop: 20 }}>
          Select dates on the calendar you wish to post an onsite interview.
        </h4>

        {/* Office Selection + Calendar */}
        <div
          style={{
            display: 'flex',
            marginTop: 20,
            justifyContent: 'center',
            gap: 100,
          }}
        >
          <ChildOfficeSelect
            parentOfficeId={posting?.officeId}
            defaultOfficeId={posting?.officeId}
            selectedOffice={selectedOffice}
            onSelectOffice={(office) => setSelectedOffice(office)}
          />

          <CalendarSelection
            maxDaysToSelect={99}
            selectedDates={selectedDates}
            onChange={(selectedDates) => setSelectedDates(selectedDates)}
          />
        </div>

        {/* Form Section */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            marginTop: 50,
          }}
        >
          <h4 style={{ textAlign: 'center' }}>Select onsite interview details</h4>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: 50,
              width: 580,
              gap: 20,
            }}
          >
            {/* Job Title */}
            <RowContainer label="Job Title:">
              <p
                className="form-control"
                style={{
                  flex: 1,
                  fontSize: 20,
                  height: 50,
                  marginBottom: 0,
                  alignContent: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'auto',
                }}
              >
                {posting?.title}
              </p>
            </RowContainer>

            {/* Profession */}
            <RowContainer label="Profession:">
              <select
                disabled
                className="form-control"
                style={{
                  width: '60%',
                  marginTop: '5px',
                  display: 'table-cell',
                  height: 50,
                  fontSize: 20,
                }}
                value={formValues.profession}
                onChange={(e) => handleProfessionChange(e.target.value)}
              >
                {allProfessions?.map((profession) => (
                  <option
                    key={`profession-${profession.code}`}
                    className="form-control"
                    style={{ fontSize: 20 }}
                    value={profession.code}
                  >
                    {profession.name}
                  </option>
                ))}
              </select>
            </RowContainer>

            {/* Sub-Profession (conditional for DA) */}
            {shouldShowSubProfession && (
              <RowContainer label="Sub-Profession:">
                <select
                  disabled
                  className="form-control"
                  style={{
                    width: '60%',
                    marginTop: '5px',
                    display: 'table-cell',
                    height: 50,
                    fontSize: 20,
                  }}
                  value={formValues.subProfession || 'none'}
                  onChange={(e) => {
                    const code = e.target.value;
                    handleChange('subProfession', code === 'none' ? null : code);
                  }}
                >
                  {daSubProfessions.map((subProfession) => (
                    <option
                      key={`subProfession-${subProfession.code}`}
                      className="form-control"
                      style={{ fontSize: 20 }}
                      value={subProfession.code}
                    >
                      {subProfession.name}
                    </option>
                  ))}
                </select>
              </RowContainer>
            )}

            {/* Preferred Skill */}
            <RowContainer label="Preferred Skill:">
              <select
                className="form-control"
                style={{
                  width: '60%',
                  marginTop: '5px',
                  display: 'table-cell',
                  height: 50,
                  fontSize: 20,
                }}
                onChange={(e) => handleChange('specialty', e.target.value)}
                value={formValues.specialty}
              >
                {availableSpecialties.map((code) => (
                  <option key={code} className="form-control" style={{ fontSize: 20 }} value={code}>
                    {code.replace(/^specialty_/g, '')}
                  </option>
                ))}
              </select>
            </RowContainer>

            {/* Arrival */}
            <RowContainer label="Arrival:">
              <input
                value={formValues.localStart}
                onChange={(val) => handleChange('localStart', val.target.value)}
                type="time"
                className="form-control"
                style={{
                  display: 'table-cell',
                  height: 50,
                  fontSize: 20,
                  width: '60%',
                }}
              />
            </RowContainer>

            {/* End */}
            <RowContainer label="End:">
              <input
                value={formValues.localEnd}
                onChange={(val) => handleChange('localEnd', val.target.value)}
                type="time"
                className="form-control"
                style={{
                  display: 'table-cell',
                  height: 50,
                  fontSize: 20,
                  width: '60%',
                }}
              />
            </RowContainer>

            {/* Hourly Offer */}
            <RowContainer label="Hourly Offer:">
              <input
                value={formValues.hourlyRate}
                onChange={(e) => handleChange('hourlyRate', e.target.value)}
                className="form-control"
                style={{ width: '60%', fontSize: 20, height: 50 }}
                placeholder="00"
              />
            </RowContainer>

            {/* Meal Break */}
            <RowContainer label="Meal Break:">
              <p
                className="form-control"
                style={{
                  width: '60%',
                  fontSize: 20,
                  height: 50,
                  marginBottom: 0,
                  alignContent: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => setShowBreakModal(true)}
              >
                {formValues.breakTime
                  ? `${formValues.breakTime} ${formValues.isBreakPaid ? '(Paid)' : '(Unpaid)'}`
                  : 'Time'}
              </p>
            </RowContainer>
          </div>

          {/* Review Button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 43 }}>
            <button
              type="button"
              onClick={handleReviewInterviews}
              className="btn btn-success"
              disabled={errorStatus?.invalidForm}
              style={{
                width: 200,
                borderRadius: 50,
                height: 50,
                fontSize: 20,
              }}
            >
              Review Interviews
            </button>
          </div>
        </div>
      </div>

      {/* Meal Break Modal */}
      <MobileAppModal
        show={showBreakModal}
        closeModal={() => setShowBreakModal(false)}
        content="break"
        result={handleBreakResult}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        previewData={previewData}
        office={selectedOffice}
        onConfirm={handleConfirmSubmission}
        onCancel={() => setShowConfirmation(false)}
        isSubmitting={isSubmitting}
      />

      {/* Bottom Spacer */}
      <div style={{ height: 100 }} />
    </div>
  );
};
